import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NoiseFrequencyRating } from 'src/app/enums/noiseFrequencyRating.enum';
import { NoiseSpeedType } from 'src/app/enums/noiseSpeedType.enum';
import { Units, UnitSymbols } from 'src/app/enums/units.enum';
import { AgentJurisdictionDialogComponent, IAgentJurisdictionData } from 'src/app/modals/agent-jurisdiction-dialog/agent-jurisdiction-dialog.component';
import { AgentJurisdictionHistoryDialogComponent } from 'src/app/modals/agent-jurisdiction-history-dialog/agent-jurisdiction-history-dialog.component';
import { AgentJurisdiction } from 'src/app/models/agent-jurisdiction.model';
import { Jurisdiction } from 'src/app/models/Jurisdiction.model';
import { AgentJurisdictionService } from 'src/app/services/agent-jurisdiction.service';
import { JurisdictionService } from 'src/app/services/jurisdiction.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { decorateAgentJurisdictionVersions } from 'src/app/_helpers/oel-version.helper';
import { GridPermissions } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';

type TabType = 'agents' | 'riskconfig' | 'bayesianconfig' | 'acceptancerules';

const TABS = {
  AGENTS: 'agents' as TabType,
  RISK_CONFIG: 'riskconfig' as TabType,
  BAYESIAN_CONFIG: 'bayesianconfig' as TabType,
  ACCEPTANCE_RULES: 'acceptancerules' as TabType
};

@Component({
  standalone: false,
  selector: 'app-jurisdiction-details-popup',
  templateUrl: './jurisdiction-details-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './jurisdiction-details-popup.component.scss'
})
export class JurisdictionDetailsPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;
  private destroy$ = new Subject<void>();

  jurisdictionDetails: Jurisdiction;
  displayedColumns: string[] = [];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  dataSource = new MatTableDataSource<AgentJurisdiction>();
  isLoading = false;
  noiseFrequencyRatingEnum = NoiseFrequencyRating;
  noiseSpeedEnum = NoiseSpeedType;
  unitsEnum = Units;
  activeTab: TabType = TABS.AGENTS;
  readonly TABS = TABS;
  readonly permissionObject = RolePermissionObjects.AgentJurisdiction;
  readonly createAction = GridPermissions.Create;
  readonly updateAction = GridPermissions.Update;
  readonly deleteAction = GridPermissions.Delete;

  constructor(
    @Inject(FLYOUT_DATA) public data: string,
    private jurisdictionService: JurisdictionService,
    private agentJurisdictionService: AgentJurisdictionService,
    private toastService: ToastService,
    protected dialog: MatDialog,
    private translateService: TranslateService,
    private valueConversionService: ValueConversionService,
    private dateFormatService: DateFormatService,
  ) {
    if (data) {
      this.getJurisdictionDetails();
    }
  }

  ngOnInit(): void {
    this.displayedColumns = [
      nameOf<AgentJurisdiction>(x => x.masterAgentId),
      nameOf<AgentJurisdiction>(x => x.al),
      nameOf<AgentJurisdiction>(x => x.oel),
      nameOf<AgentJurisdiction>(x => x.oelTypeName),
      nameOf<AgentJurisdiction>(x => x.ceilingLimit),
      nameOf<AgentJurisdiction>(x => x.stel),
      nameOf<AgentJurisdiction>(x => x.exchangeRate),
      nameOf<AgentJurisdiction>(x => x.threshold),
      nameOf<AgentJurisdiction>(x => x.noiseFrequencyRating),
      nameOf<AgentJurisdiction>(x => x.noiseSpeed),
      'effectivePeriod',
      'status',
      'actions'
    ];
  }

  getJurisdictionDetails() {
    this.isLoading = true;
    this.jurisdictionService.getById(this.data).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.jurisdictionDetails = response;
        this.getJurisdictions();
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isLoading = false;
      }
    });
  }

  getJurisdictions() {
    this.agentJurisdictionService.getJurisdictionDetails(this.jurisdictionDetails.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const decorated = decorateAgentJurisdictionVersions(response, this.dateFormatService, this.translateService);
          this.dataSource.data = decorated.map(res => ({ ...res, oelValue: this.getOelValue(res) }));
          this.isLoading = false;
        },
        error: (error) => {
          this.toastService.showError(error);
          this.dataSource.data = [];
          this.isLoading = false;
        }
      });
  }

  getOelValue(agent: AgentJurisdiction) {
    if (agent.oel == null) return '-';
    const hasUnit = agent.agentUnits != null;
    const val = agent.agentUnits != null
      ? this.valueConversionService.getParameterValueAsPerAgentUnit(agent.oel, agent.agentUnits)
      : agent.oel;
    return `${+val.toFixed(4)}${hasUnit ? ' ' + UnitSymbols[agent.agentUnits] : ''}`;
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onSortChanged(sort: Sort) {
    /* Need to confirm if Api Sorting for getJurisdictions will be there */
  }

  onTabChange(tab: TabType): void {
    this.activeTab = tab;
  }

  openAgentJurisdictionDialog(agentJurisdictionId?: string, isReadonly = false) {
    const data: IAgentJurisdictionData = {
      id: agentJurisdictionId ?? '',
      jurisdictionId: this.jurisdictionDetails.id,
      isReadonly
    }
    const dialogRef = this.dialog.open(AgentJurisdictionDialogComponent, {
      data: data,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    this.closeDialog(dialogRef);
  }

  openVersionHistory(agentJurisdictionId: string) {
    this.dialog.open(AgentJurisdictionHistoryDialogComponent, {
      data: agentJurisdictionId,
      width: '900px',
      maxWidth: '90vw',
    });
  }

  openNewVersionDialog(agentJurisdictionId: string) {
    const data: IAgentJurisdictionData = {
      id: agentJurisdictionId,
      jurisdictionId: this.jurisdictionDetails.id,
      isNewVersion: true
    }
    const dialogRef = this.dialog.open(AgentJurisdictionDialogComponent, {
      data: data,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    this.closeDialog(dialogRef);
  }

  openAgentJurisdictionDeleteDialog(id?: string) {
    // Guard: historical (closed) OEL versions must not be mutated/deleted.
    if (this.dataSource.data.find(r => r.id === id)?.isHistorical) {
      return;
    }
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: {
        id: id,
        formDeletes: true,
        service: this.agentJurisdictionService,
        title: this.translateService.getDynamicTranslation('Agent Jurisdiction')
      },
      width: '440px',
      disableClose: true
    });
    this.closeDialog(dialogRef);
  }

  closeDialog(dialogRef: MatDialogRef<unknown>): void {
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: unknown) => {
      if (result) this.getJurisdictions();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
